import { Menu, useComputedColorScheme } from '@mantine/core';
import { moderatorUserLookupPath } from '~/shared/constants/moderator-app';
import { openConfirmModal } from '@mantine/modals';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import { showNotification, updateNotification } from '@mantine/notifications';
import {
  IconArrowBackUp,
  IconBan,
  IconCoin,
  IconCrystalBall,
  IconDotsVertical,
  IconFlag,
  IconGift,
  IconGraphOff,
  IconGraph,
  IconEye,
  IconMicrophone,
  IconMicrophoneOff,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import React from 'react';
import { useAccountContext } from '~/components/CivitaiWrapped/AccountProvider';
import { openAddToHubModal } from '~/components/Dialog/triggers/add-to-hub';
import { openHiddenImagesFromUserModal } from '~/components/Dialog/triggers/hidden-images-from-user';
import { openReportModal } from '~/components/Dialog/triggers/report';
import { dialogStore } from '~/components/Dialog/dialogStore';
import { BlockUserButton } from '~/components/HideUserButton/BlockUserButton';
import { SuspendPlacerMenuItem } from '~/components/Sticker/SuspendPlacerMenuItem';
import { HideUserButton } from '~/components/HideUserButton/HideUserButton';
import { LoginRedirect } from '~/components/LoginRedirect/LoginRedirect';
// import { ProfileHeader } from '~/components/Profile/ProfileHeader';
// import ProfileLayout from '~/components/Profile/ProfileLayout';
import UserBanModal from '~/components/Profile/UserBanModal';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useHiddenPreferencesData } from '~/hooks/hidden-preferences';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { constants } from '~/server/common/constants';
import { ReportEntity } from '~/shared/utils/report-helpers';
import { showErrorNotification, showWarningNotification } from '~/utils/notifications';
import { postgresSlugify } from '~/utils/string-helpers';
import { trpc } from '~/utils/trpc';
import { LegacyActionIcon } from '~/components/LegacyActionIcon/LegacyActionIcon';
import { AddToHubMenuItem } from '~/components/MenuItems/AddToHubMenuItem';
import { ModeratorLookupMenuItem } from '~/components/Moderation/ModeratorLookupMenuItem';
import { UserHubSourceType } from '~/shared/utils/prisma/enums';

export const UserContextMenu = ({ username }: { username: string }) => {
  const queryUtils = trpc.useUtils();
  const currentUser = useCurrentUser();
  const features = useFeatureFlags();
  const { impersonate } = useAccountContext();
  // A hidden profile cover is filtered out of the header along with its own
  // context menu, so this menu is the only place left to reach it from.
  const hasHiddenImages = useHiddenPreferencesData().hiddenImages.some((x) => x.hidden);

  const { data: user, isLoading: userLoading } = trpc.user.getCreator.useQuery(
    { username },
    { enabled: username !== constants.system.user.username }
  );
  const isMod = currentUser && currentUser.isModerator;
  const isSameUser =
    !!currentUser &&
    !!currentUser.username &&
    postgresSlugify(currentUser.username) === postgresSlugify(username);
  const removeContentMutation = trpc.user.removeAllContent.useMutation();

  const toggleBanMutation = trpc.user.toggleBan.useMutation({
    async onMutate() {
      await queryUtils.user.getCreator.cancel({ username });

      const prevUser = queryUtils.user.getCreator.getData({ username });
      queryUtils.user.getCreator.setData({ username }, () =>
        prevUser
          ? {
              ...prevUser,
              bannedAt: prevUser.bannedAt ? null : new Date(),
            }
          : undefined
      );

      return { prevUser };
    },
    async onSuccess() {
      await queryUtils.userProfile.get.invalidate({ username });
    },
    onError(_error, _vars, context) {
      queryUtils.user.getCreator.setData({ username }, context?.prevUser);
      showErrorNotification({
        error: new Error('Unable to ban user, please try again.'),
      });
    },
  });

  const toggleMuteMutation = trpc.user.toggleMute.useMutation({
    async onMutate() {
      await queryUtils.user.getCreator.cancel({ username });

      const prevUser = queryUtils.user.getCreator.getData({ username });
      queryUtils.user.getCreator.setData({ username }, () =>
        prevUser
          ? {
              ...prevUser,
              muted: !prevUser.muted,
            }
          : undefined
      );

      return { prevUser };
    },
    onError(_error, _vars, context) {
      queryUtils.user.getCreator.setData({ username }, context?.prevUser);
      showErrorNotification({
        error: new Error('Unable to mute user, please try again.'),
      });
    },
    onSuccess(data) {
      // The mute stands; clicking again would undo it, so this is a warning, not an error.
      if (data.sessionInvalidated === false)
        showWarningNotification({
          title: 'Muted, but still signed in',
          message: `${username} is muted, but their open sessions could not be signed out. The mute is in effect.`,
          autoClose: false,
        });
    },
  });

  const canManageUserPayments = !!features.userPaymentConfiguration;

  const { data: tipaltiStatus } = trpc.user.getTipaltiStatus.useQuery(
    { id: user?.id as number },
    { enabled: canManageUserPayments && !!user?.id }
  );

  const enableTipaltiMutation = trpc.user.enableTipalti.useMutation({
    onSuccess() {
      showNotification({
        title: 'Tipalti Enabled',
        message: `Tipalti has been enabled for ${username}`,
      });
      queryUtils.user.getTipaltiStatus.invalidate({ id: user?.id as number });
    },
    onError(error) {
      showErrorNotification({
        error: new Error(error.message ?? 'Unable to enable Tipalti, please try again.'),
      });
    },
  });

  const resetSubscriptionCachesMutation = trpc.user.resetSubscriptionCaches.useMutation({
    onSuccess() {
      showNotification({
        title: 'Caches Cleared',
        message: `Membership caches have been cleared for ${username}`,
      });
    },
    onError(error) {
      showErrorNotification({
        error: new Error(error.message ?? 'Unable to clear caches, please try again.'),
      });
    },
  });

  const toggleLeaderboardMutation = trpc.user.setLeaderboardEligibility.useMutation({
    async onMutate({ setTo }) {
      await queryUtils.user.getCreator.cancel({ username });

      const prevUser = queryUtils.user.getCreator.getData({ username });
      queryUtils.user.getCreator.setData({ username }, () =>
        prevUser ? { ...prevUser, excludeFromLeaderboards: setTo } : undefined
      );

      return { prevUser };
    },
    onError(_error, _vars, context) {
      queryUtils.user.getCreator.setData({ username }, context?.prevUser);
      showErrorNotification({
        error: new Error('Unable to remove user from leaderboards, please try again.'),
      });
    },
  });

  const colorScheme = useComputedColorScheme('dark');

  const handleToggleMute = () => {
    if (user) toggleMuteMutation.mutate({ id: user.id });
  };
  const handleToggleBan = () => {
    if (user) {
      if (user.bannedAt) toggleBanMutation.mutate({ id: user.id });
      else
        dialogStore.trigger({
          component: UserBanModal,
          props: { userId: user.id, username: user.username as string },
        });
    }
  };
  const handleToggleLeaderboardEligibility = () => {
    if (!user) return;
    if (user.excludeFromLeaderboards)
      toggleLeaderboardMutation.mutate({ id: user.id, setTo: !user.excludeFromLeaderboards });
    else
      openConfirmModal({
        title: 'Remove from Leaderboards',
        children: `Are you sure you want to remove this user from leaderboards? This will take effect at the next refresh.`,
        labels: { confirm: 'Yes, remove from leaderboards', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () =>
          toggleLeaderboardMutation.mutate({ id: user.id, setTo: !user.excludeFromLeaderboards }),
      });
  };
  // const handleRemoveContent = () => {
  //   if (!user) return;
  //   openConfirmModal({
  //     title: 'Remove All Content',
  //     children: `Are you sure you want to remove all content (models, reviews, comments, posts, and images) by this user? This action cannot be undone.`,
  //     labels: { confirm: 'Yes, remove all content', cancel: 'Cancel' },
  //     confirmProps: { color: 'red' },
  //     onConfirm: () => removeContentMutation.mutate({ id: user.id }),
  //   });
  // };
  const handleEnableTipalti = () => {
    if (user) enableTipaltiMutation.mutate({ id: user.id });
  };

  const handleResetSubscriptionCaches = () => {
    if (user) resetSubscriptionCachesMutation.mutate({ id: user.id });
  };

  const handleImpersonate = async () => {
    if (!user || !currentUser || !features.impersonation || user.id === currentUser?.id) return;
    const notificationId = `impersonate-${user.id}`;

    showNotification({
      id: notificationId,
      loading: true,
      autoClose: false,
      title: 'Switching accounts...',
      message: `-> ${user.username as string} (${user.id})`,
    });

    // Hub-native impersonation (F), centralized in the account context — authorized by mod status alone; the
    // proxy mints + sets the civ-token (stamped impersonatedBy). No client-held token, no ogAccount.
    try {
      await impersonate(user.id); // reloads as the impersonated user on success
    } catch (e) {
      updateNotification({
        id: notificationId,
        icon: <IconX size={18} />,
        color: 'red',
        title: 'Failed to switch',
        message: (e as Error).message,
      });
    }
  };

  if (userLoading || !user) {
    return null;
  }

  return (
    <Menu position="left-start" withinPortal>
      <Menu.Target>
        <LegacyActionIcon
          loading={removeContentMutation.isPending}
          size={30}
          radius="xl"
          color="gray"
          variant={colorScheme === 'dark' ? 'filled' : 'light'}
          ml="auto"
        >
          <IconDotsVertical size={16} />
        </LegacyActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <>
          {isMod && (
            <>
              <ModeratorLookupMenuItem path={moderatorUserLookupPath(user.id)}>
                Lookup User
              </ModeratorLookupMenuItem>
              {features.impersonation && user.id !== currentUser.id && (
                <Menu.Item
                  color="yellow"
                  leftSection={<IconCrystalBall size={14} stroke={1.5} />}
                  onClick={handleImpersonate}
                >
                  Impersonate User
                </Menu.Item>
              )}
              <Menu.Item
                color={user.bannedAt ? 'green' : 'red'}
                leftSection={
                  !user.bannedAt ? (
                    <IconBan size={14} stroke={1.5} />
                  ) : (
                    <IconArrowBackUp size={14} stroke={1.5} />
                  )
                }
                onClick={handleToggleBan}
              >
                {user.bannedAt ? 'Restore user' : 'Ban user'}
              </Menu.Item>
              <Menu.Item
                color={user.excludeFromLeaderboards ? 'green' : 'red'}
                leftSection={
                  !user.excludeFromLeaderboards ? (
                    <IconGraphOff size={14} stroke={1.5} />
                  ) : (
                    <IconGraph size={14} stroke={1.5} />
                  )
                }
                onClick={handleToggleLeaderboardEligibility}
              >
                {user.excludeFromLeaderboards
                  ? 'Include in leaderboards'
                  : 'Exclude from leaderboards'}
              </Menu.Item>
              {/* <Menu.Item
                color="red"
                leftSection={<IconTrash size={14} stroke={1.5} />}
                onClick={handleRemoveContent}
              >
                Remove all content
              </Menu.Item> */}
              <Menu.Item
                leftSection={
                  user.muted ? (
                    <IconMicrophone size={14} stroke={1.5} />
                  ) : (
                    <IconMicrophoneOff size={14} stroke={1.5} />
                  )
                }
                onClick={handleToggleMute}
              >
                {user.muted ? 'Unmute user' : 'Mute user'}
              </Menu.Item>
              <SuspendPlacerMenuItem userId={user.id} />
              {canManageUserPayments && (
                <>
                  <Menu.Item
                    leftSection={<IconCoin size={14} stroke={1.5} />}
                    onClick={handleEnableTipalti}
                    disabled={tipaltiStatus?.enabled || enableTipaltiMutation.isPending}
                  >
                    {tipaltiStatus?.enabled ? 'Tipalti Enabled' : 'Enable Tipalti'}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconRefresh size={14} stroke={1.5} />}
                    onClick={handleResetSubscriptionCaches}
                    disabled={resetSubscriptionCachesMutation.isPending}
                  >
                    Clear Membership Cache
                  </Menu.Item>
                </>
              )}
            </>
          )}
          {features.giftMemberships && currentUser && (
            <Menu.Item
              leftSection={<IconGift size={14} stroke={1.5} />}
              component={Link}
              href={`/pricing/gift?userId=${user.id}`}
            >
              Gift a membership
            </Menu.Item>
          )}
          <AddToHubMenuItem
            onClick={() =>
              openAddToHubModal({
                props: {
                  source: {
                    type: UserHubSourceType.User,
                    targetId: user.id,
                    alias: user.username,
                  },
                },
              })
            }
          />
          {!isSameUser && <BlockUserButton userId={user.id} as="menu-item" />}
          {isSameUser && (
            <Menu.Item component={Link} href={`/user/${username}/manage-categories`}>
              Manage model categories
            </Menu.Item>
          )}
          {hasHiddenImages && (
            <Menu.Item
              leftSection={<IconEye size={14} stroke={1.5} />}
              onClick={() =>
                openHiddenImagesFromUserModal({
                  props: { userId: user.id, username: user.username },
                })
              }
            >
              Review hidden images
            </Menu.Item>
          )}
          <HideUserButton as="menu-item" userId={user.id} />
          {user.id !== currentUser?.id && (
            <LoginRedirect reason="report-user">
              <Menu.Item
                leftSection={<IconFlag size={14} stroke={1.5} />}
                onClick={() =>
                  openReportModal({
                    entityType: ReportEntity.User,
                    entityId: user.id,
                  })
                }
              >
                Report
              </Menu.Item>
            </LoginRedirect>
          )}
        </>
      </Menu.Dropdown>
    </Menu>
  );
};
